import { SuperSaveSystem } from '@game-libs/super-save-system';
import { CONFIG } from '../config';
import { EMPTY_GUI_GAME_STATE, GUIGameState } from '../types/game-types';
import { EXPECTED_SAVE_VERSION } from './zustand/useGameStatusStore';

const { save, load } = SuperSaveSystem();

/** When true, saveState is a no-op. Set during clear-save to prevent a deferred save from overwriting the clear. */
let isClearingSave = false;

export function setClearingSave(value: boolean): void {
  isClearingSave = value;
}

/** Fields persisted by Zustand stores; omit from main save blob to avoid duplication. */
const ZUSTAND_OWNED_FIELDS = [
  'dragonHealth',
  'remainingPoison',
  'damageLastTick',
  'gold',
  'wood',
  'ore',
  'food',
  'mana',
  'goldLastTick',
  'inspiration',
  'dungeonCrawler',
  /** Persisted via useTimedEventsStore (`timed-events` key). */
  'timedEvents',
  /** Persisted via useGeneratorsStore (`generators` key). */
  'generators',
  /** Persisted via useUpgradesStore (`upgrades` key). */
  'upgrades',
] as const;

/** Strip Zustand-owned keys from a raw loaded object so reducer state is filled from stores instead. */
function stripZustandOwnedFields(raw: Record<string, unknown>): Record<string, unknown> {
  const thin = { ...raw };
  for (const key of ZUSTAND_OWNED_FIELDS) {
    delete thin[key];
  }
  // Old monolithic saves may still carry this; game status lives only in useGameStatusStore now.
  delete thin.gameStatus;
  // saveVersion / tickLength / buyMax are owned by useGameStatusStore (persist); keep blob thin.
  delete thin.saveVersion;
  delete thin.tickLength;
  delete thin.gameSettings;
  return thin;
}

export const saveState = (state: GUIGameState) => {
  if (CONFIG.save.skipSave || isClearingSave) return;

  setTimeout(() => {
    if (isClearingSave) return; // Guard again: deferred save might run after clear
    const stateToSave = { ...state } as Record<string, unknown>;
    for (const key of ZUSTAND_OWNED_FIELDS) {
      delete stateToSave[key];
    }
    // Keep on the blob for schema migration checks (authoritative copy also lives in useGameStatusStore).
    stateToSave.saveVersion = EXPECTED_SAVE_VERSION;
    save(CONFIG.save.saveKey, stateToSave);
    save(CONFIG.save.timestampKey, Date.now().toString());
  }, 0);
};

/**
 * Loads non-currency game blob.
 * Third tuple value: whether saveVersion mismatched (triggers full reset in init-state).
 * Fourth: legacy `generators` from monolithic saves (pre–Zustand), if present.
 * Fifth: legacy `upgrades` from monolithic saves, if present.
 */
export const loadState = async (): Promise<[GUIGameState, number, boolean, unknown, unknown]> => {
  const rawLoaded = await load(CONFIG.save.saveKey, EMPTY_GUI_GAME_STATE);
  const timestamp = await load(CONFIG.save.timestampKey, Date.now().toString());
  save(CONFIG.save.timestampKey, Date.now().toString());

  // Only mismatch when the blob explicitly carried a version and it differs (fresh default state has no saveVersion).
  const blobSaveVersion = (rawLoaded as { saveVersion?: string } | null)?.saveVersion;
  const versionMismatch =
    blobSaveVersion !== undefined && blobSaveVersion !== EXPECTED_SAVE_VERSION;

  let secondsOffline = 0;

  const rawRecord =
    rawLoaded && typeof rawLoaded === 'object' && !Array.isArray(rawLoaded) ? (rawLoaded as Record<string, unknown>) : null;

  let mergeSource: Record<string, unknown>;

  if (versionMismatch) {
    console.warn(
      `Save version mismatch, resetting state... Loaded ${blobSaveVersion} !== Expected ${EXPECTED_SAVE_VERSION}`,
    );

    // Belt-and-suspenders: init-state also resets GlobalGameStore in memory before game.start.
    localStorage.removeItem(import.meta.env.VITE_GAME_PREFIX + '_global-save');

    mergeSource = { ...EMPTY_GUI_GAME_STATE } as unknown as Record<string, unknown>;
  } else {
    mergeSource = stripZustandOwnedFields((rawRecord ?? {}) as Record<string, unknown>);
    console.log('Save version match! Loading save game data...', rawLoaded.saveVersion ?? EXPECTED_SAVE_VERSION);

    if (timestamp) {
      secondsOffline = Math.floor((Date.now() - Number(timestamp)) / 1000);
    }
  }

  const state: GUIGameState = {
    ...structuredClone(EMPTY_GUI_GAME_STATE),
    ...(mergeSource as GUIGameState),
  };

  const legacyGeneratorsFromMainBlob = versionMismatch ? undefined : rawRecord?.generators;
  const legacyUpgradesFromMainBlob = versionMismatch ? undefined : rawRecord?.upgrades;

  return [state, secondsOffline, versionMismatch, legacyGeneratorsFromMainBlob, legacyUpgradesFromMainBlob];
};
